import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faCircleInfo, faClockRotateLeft, faLink } from '@fortawesome/pro-duotone-svg-icons';

import { ChipInputComponent, type ChipInputChip, type ChipInputSuggestion } from '../chip-input/chip-input.component';
import { PlanLimitComponent } from '../plan-limit/plan-limit.component';
import { ShareDialogService } from './share-dialog.service';
import { ShareService } from '../../services/share.service';
import { MEMBER_SEARCH_WINDOW } from '../../services/member.service';
import { TemporalContextService } from '../../services/temporal-context.service';
import { LocaleService } from '../../services/locale.service';
import { AppConfigService } from '../../services/app-config.service';
import { MutationError, resolveErrorMessage, type UserError } from '@branduals/graphql-errors';
import { copyLinkWithToasts } from '../../utils/clipboard.utils';
import { pluralKey } from '../../utils/plural.utils';
import { publicGrantReaches } from '../../utils/public-reach';
import { isPublicFileRoad } from '../../utils/url.utils';
import { confirmDiscard, registerUnsavedWork } from '@branduals/dialog-guard';
import { isSubmitChord } from '../../utils/keyboard.utils';
import { SubmitChordHintComponent } from '../submit-chord-hint/submit-chord-hint.component';
import { CharCounterComponent } from '../char-counter/char-counter.component';
import { GuardedDialogHeaderComponent } from '@branduals/dialog-guard';
import { DialogEscapeDirective } from '@branduals/dialog-guard';
import { DisplayDatePipe } from '../../pipes/display-date.pipe';

/** The share prompt speaks in the dialog's own voice: it is recipients and a message, not a draft. */
const DISCARD_SHARE_COPY = { headerKey: 'share.discardShare.header', messageKey: 'share.discardShare.message' };

/** FE mirrors of server clamps (server-authoritative; these only shape UX). */
const SHARE_RECIPIENT_CAP = 100;   // the invite batch bound both share lanes clamp to
const MESSAGE_MAX_LENGTH = 2000;   // the invitation personal-message cap
const SEARCH_DEBOUNCE_MS = 250;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // member-invite precedent

type SharePhase = 'configure' | 'done' | 'partial' | 'failed';

/**
 * The universal share dialog — ONE primitive across the pillars. Surfaces open it through
 * ShareDialogService with `{ destinationUrl, destinationTitle, subjectRecordId, publicState }`; the single
 * entry field is the shared chip-input, whose member suggestions ride the share-recipient
 * search — the people a share can reach (active (approved, not paused), human, mailbox-bearing members; the caller
 * never offered), served by the one home the @-mention picker rides too, so nothing is thinned
 * here after the read: agents never come back (they carry no mailbox; admission is a human
 * consent act) and neither does a pending member. A typed email that exactly matches a member
 * silently becomes that member's chip. The field is a people picker in chip form and speaks the
 * family's ONE window (ruled 2026-09-09): it opens on focus, every read asks for the member
 * service's one constant, nothing typed is a floor, and the served window minus the people
 * already chipped is what the listbox offers — the dialog once carried a two-character floor,
 * two read sizes and a shown cap of its own, in bare numbers nothing explained.
 *
 * Send is ONE gesture fanning out to `shareWithMembers` (member chips) and `shareInvite`
 * (email chips), each independently idempotent under one per-open key — safe because each
 * mutation deduplicates under its own kind. Send awaits the server before showing the
 * outcome (share/invite sends are an optimistic-UX exception class): all delivered → done
 * (a repeat share re-sends the sharer's own earlier invitation — still "sent"; a recipient
 * already waiting for admin approval is reported as such, nothing new sent to them);
 * some refused → partial when someone new received something, failed when nobody did (the
 * people already waiting and the ones the plan had no room for received nothing new); either
 * keeps ONLY the refused people for a fresh-key retry, and lists the ones the plan had no room
 * for apart, never retried.
 *
 * The dialog owns its close gesture: the X, Cancel and Escape all funnel through `guardedClose()`,
 * which asks "Discard this share?" while recipients, a message, or typed-but-uncommitted text
 * would be lost; the result phases (done, partial and failed), and an in-flight send, close silently.
 * The two doors that leave for another page — "View shares I sent" and the limit face's "See plans" —
 * close it directly on their way: both stand only in a result, and the dialog, mounted once at the
 * app root, outlives every route change (a retry still owed goes with it, as it does on Close).
 */
@Component({
  selector: 'app-share-dialog',
  standalone: true,
  imports: [
    DisplayDatePipe,
    DialogModule, ButtonModule, ConfirmDialogModule, TextareaModule, TooltipModule,
    TranslateModule, FontAwesomeModule, ChipInputComponent, GuardedDialogHeaderComponent, DialogEscapeDirective,
    SubmitChordHintComponent, CharCounterComponent, PlanLimitComponent,
  ],
  // A LOCAL ConfirmationService + mount: the root instance already has a bound mount (the
  // collection dock's keyless dialog) — a dialog that skipped its own provider would render its
  // discard prompt through that foreign chrome.
  providers: [ConfirmationService],
  templateUrl: './share-dialog.component.html',
  styleUrls: ['./share-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareDialogComponent {
  protected readonly dialog = inject(ShareDialogService);
  private readonly router = inject(Router);
  private readonly share = inject(ShareService);
  protected readonly temporal = inject(TemporalContextService);
  private readonly locale = inject(LocaleService);
  private readonly appConfig = inject(AppConfigService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);

  protected readonly faCheck = faCheck;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faClockRotateLeft = faClockRotateLeft;
  protected readonly faLink = faLink;

  protected readonly recipientCap = SHARE_RECIPIENT_CAP;
  protected readonly messageMaxLength = MESSAGE_MAX_LENGTH;

  /**
   * The Copy-link pair (the control's tooltip; the success toast's reach line) follows whether the internet can open
   * the copied link NOW — the one public-reach predicate the public marker names by: the Public audience's grant
   * reaches the subject, the subject is reachable AT ITS OWN URL — the module road alone (published in some language the
   * brand offers over a public path up to the module root, licensed, not trashed
   * — judged across every offered language, never the reader's own) and the brand's switch is on. Otherwise the
   * members-only pair: a caller for whom the public state is not resolved (null), an
   * unpublished draft inside a public gallery, an OFF brand (every brand at birth), and an item the brand portal serves
   * with no grant of its own, public on the portal's own path and never at the URL this dialog copies — and (Robert's ruling of
   * 2026-09-26) a public file copied on a road the internet cannot open: its own Drive route, a search or home route,
   * a workspace; the public pair renders over a public-module container road alone. Four full key literals, never
   * a composed key: the orphan guard reads keys as literals.
   */
  protected readonly copyLinkScope = computed(() =>
    publicGrantReaches(this.dialog.context()?.publicState, this.appConfig.config$()?.publicAccess === true)
      && isPublicFileRoad(this.dialog.context()?.destinationUrl ?? '')
      ? { tooltip: 'share.copyLinkScopePublic', toast: 'share.copyLinkScopeToastPublic' }
      : { tooltip: 'share.copyLinkScope', toast: 'share.copyLinkScopeToast' });

  protected readonly phase = signal<SharePhase>('configure');
  protected readonly sending = signal(false);
  protected readonly recipients = signal<ChipInputChip[]>([]);
  protected readonly message = signal('');
  protected readonly suggestions = signal<ChipInputSuggestion[]>([]);
  /**
   * The suggestion read's lifecycle for the CURRENT query — the ground for the no-match face. A read
   * "settles empty" on RAW rows, before the projection drops agents and already-picked people: a member
   * re-typing an existing recipient's exact name must never be told nobody by that name exists. A FAILED
   * read is excluded — "No one by that name yet" over a failed read would be a false definite claim — and
   * so is a BARE browse (nothing typed: the field opens on focus and lists its window): "that name" needs
   * a name, so an empty brand browses to an empty list and claims nobody.
   */
  private readonly suggestionsSettledFor = signal<string | null>(null);
  private readonly suggestionsRawCount = signal(0);
  /** Protected: the under-field hint renders the search-down face off it — a failed people-search
   *  must not wear silence (it reads as "still searching") nor the no-match claim (a false definite);
   *  its own sentence also explains why typing a member's email can bounce while search is down. */
  protected readonly suggestionsFailed = signal(false);
  protected readonly suggestionsSettledEmpty = computed(() => {
    const settledFor = this.suggestionsSettledFor();
    return settledFor !== null && settledFor.length > 0 && this.suggestionsRawCount() === 0 && !this.suggestionsFailed();
  });
  protected readonly failureMessages = signal<string[]>([]);
  /**
   * The people the brand had no room for: a share-invite row refused for a seat (its code; its address in the row's
   * argument) is not a failure to retry — sending again cannot succeed until there is room — so these leave the retry
   * set and stand under the ONE limit face, mounted inline in the result once for the batch: its members sentence with
   * its doors by the sharer's standing ("See plans" for one who may manage billing, "Ask an admin." for the rest), the
   * invite dialog's own grammar. A non-empty list is what mounts the face.
   */
  protected readonly seatRefusedChips = signal<ChipInputChip[]>([]);
  /** Whether anyone is left to send again: Retry (and the summary that promises it) shows only then. */
  protected readonly retryable = computed(() => this.recipients().length > 0);
  /** Recipients who already accepted this subject's share and sit in the admin queue —
   *  nothing new was sent to them (their own honest done-state line, never a failure). */
  protected readonly alreadyWaiting = signal<string[]>([]);
  /** Whether the gesture actually delivered anything beyond the already-waiting people —
   *  the "Sent." headline must never render over a nothing-was-sent outcome. */
  protected readonly anythingSent = signal(true);

  /** One idempotency key per open; a retry after refusals is a NEW gesture (fresh key). */
  private idempotencyKey = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchToken = 0;

  private readonly chipInput = viewChild(ChipInputComponent);

  protected readonly brandName = computed(() => this.appConfig.config$()?.brandName ?? '');

  /** Workflow subjects carry comment-write with the viewer grant; content subjects read only. */
  protected readonly disclosureKey = computed(() => {
    const url = this.dialog.context()?.destinationUrl ?? '';
    const workflow = url.startsWith('/selects') || url.startsWith('/reviews') || url.startsWith('/campaigns');
    return workflow ? 'share.disclosureComment' : 'share.disclosureView';
  });

  /** Locale-correct "A and B" join of the recipient labels for the disclosure line. */
  protected readonly disclosureName = computed(() => {
    const labels = this.recipients().map(c => c.label);
    if (labels.length === 0) return '';
    return new Intl.ListFormat(this.locale.activeUILanguage(), { style: 'long', type: 'conjunction' })
      .format(labels);
  });

  // Uncommitted typed text opens the gate too — the one-address gesture never types a
  // delimiter, so gating on chips alone reads as a broken Send. send() commits the
  // residue first; invalid text blocks there with the field highlighted.
  protected readonly canSend = computed(() =>
    (this.recipients().length > 0 || (this.chipInput()?.hasPendingText() ?? false)) && !this.sending());

  constructor() {
    // Fresh form + fresh idempotency key on every open (clone of the share-link dialog shape).
    effect(() => {
      if (this.dialog.visible()) this.reset();
    });
    // The ONE app-chrome share dialog survives every route change, so a route leave destroys
    // nothing here — only a tab close/reload loses a composed share. Its own voice travels with
    // the entry (the predicate is phase- and sending-gated by construction).
    registerUnsavedWork({
      isDirty: () => this.isDirty(),
      scope: 'unload-only',
    });
  }

  private reset(): void {
    this.phase.set('configure');
    this.sending.set(false);
    this.recipients.set([]);
    this.message.set('');
    this.suggestions.set([]);
    this.resetSuggestionState();
    this.failureMessages.set([]);
    this.seatRefusedChips.set([]);
    this.alreadyWaiting.set([]);
    this.anythingSent.set(true);
    this.idempotencyKey = crypto.randomUUID();
  }

  private resetSuggestionState(): void {
    this.suggestionsSettledFor.set(null);
    this.suggestionsRawCount.set(0);
    this.suggestionsFailed.set(false);
  }

  protected readonly emailValidator = (text: string): boolean => EMAIL_PATTERN.test(text);

  /** A typed email that exactly matches a member silently becomes that member's chip. */
  protected readonly resolveTypedEmail = async (text: string): Promise<ChipInputChip | null> => {
    try {
      const rows = await this.share.searchRecipients(text, MEMBER_SEARCH_WINDOW);
      const exact = rows.find(r => r.email.toLowerCase() === text);
      return exact ? { key: exact.id, label: exact.name || exact.email, kind: 'member' } : null;
    } catch {
      return null; // resolution failure degrades to an email chip — entry never blocks
    }
  };

  protected onQuery(text: string): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    const query = text.trim();
    const token = ++this.searchToken;
    // A new query is unsettled until its own read lands — the previous query's verdict never carries over.
    // Every query reads, the empty one included (a bare browse on focus lists the window): the family
    // has no typed-character floor.
    this.resetSuggestionState();
    this.searchTimer = setTimeout(() => void this.loadSuggestions(query, token), SEARCH_DEBOUNCE_MS);
  }

  private async loadSuggestions(query: string, token: number): Promise<void> {
    try {
      // The recipient read serves exactly the people a share can reach (the send gate's own
      // population: active (approved, not paused), human, mailbox-bearing), so nothing is thinned
      // here after the read. The people already chipped are hidden from the suggestions — the
      // dialog's own state — while the settled-empty fact reads the SERVED count, so re-typing a
      // chipped person's name never claims "no one by that name". The listbox offers the whole
      // served window (it scrolls); a shown cap beside the one window would be a second window.
      const rows = await this.share.searchRecipients(query, MEMBER_SEARCH_WINDOW);
      if (token !== this.searchToken) return; // a newer query superseded this response
      const chipped = new Set(this.recipients().map(c => c.key));
      this.suggestions.set(rows
        .filter(r => !chipped.has(r.id))
        .map(r => ({
          chip: { key: r.id, label: r.name || r.email, kind: 'member' },
          label: r.name || r.email,
          sublabel: r.email,
        })));
      this.suggestionsRawCount.set(rows.length);
      this.suggestionsFailed.set(false);
      this.suggestionsSettledFor.set(query);
    } catch {
      if (token !== this.searchToken) return; // a superseded query's late failure must not poison the current one
      this.suggestions.set([]); // suggestion failure is silent — typed emails still work
      this.suggestionsFailed.set(true);
      this.suggestionsSettledFor.set(query);
    }
  }

  protected copyLink(): void {
    const url = this.dialog.context()?.destinationUrl;
    if (!url) return;
    void copyLinkWithToasts(window.location.origin + url, this.messageService, this.translate,
      this.translate.instant(this.copyLinkScope().toast));
  }

  protected async send(): Promise<void> {
    const context = this.dialog.context();
    if (!context || this.sending()) return;
    const chipInput = this.chipInput();
    if (chipInput && !(await chipInput.commitPending())) return; // invalid residual text stays visible
    const recipients = this.recipients();
    if (recipients.length === 0) return;

    const members = recipients.filter(c => c.kind === 'member');
    const emails = recipients.filter(c => c.kind === 'email');
    const base = {
      subjectRecordId: context.subjectRecordId,
      destinationUrl: context.destinationUrl,
      message: this.message().trim() || null,
      idempotencyKey: this.idempotencyKey,
    };

    this.sending.set(true);
    const [memberResult, inviteResult] = await Promise.allSettled([
      members.length > 0
        ? this.share.shareWithMembers({ recipientUserIds: members.map(c => c.key), ...base })
        : Promise.resolve(null),
      emails.length > 0
        ? this.share.shareInvite({ recipientEmails: emails.map(c => c.key), ...base })
        : Promise.resolve(null),
    ]);
    this.sending.set(false);

    const failedKeys = new Set<string>();
    const seatRefused = new Set<string>();
    const failures: UserError[] = [];

    if (memberResult.status === 'rejected') {
      members.forEach(c => failedKeys.add(c.key));
      failures.push(...this.errorsFromThrown(memberResult.reason));
    } else if (memberResult.value) {
      if (!memberResult.value.success && memberResult.value.errors.length === 0) {
        // The server declared the gesture unsuccessful without rows — 'Sent.' must never
        // render over a declared failure.
        members.forEach(c => failedKeys.add(c.key));
        failures.push({ code: 'INTERNAL_ERROR', message: this.translate.instant('common.errorGeneric') });
      }
      for (const error of memberResult.value.errors) {
        failures.push(error);
        const failedId = error.args?.['userId'];
        if (failedId) failedKeys.add(failedId);
        else members.forEach(c => failedKeys.add(c.key)); // gesture-level refusal fails the lane
      }
    }

    let waiting: string[] = [];
    if (inviteResult.status === 'rejected') {
      emails.forEach(c => failedKeys.add(c.key));
      failures.push(...this.errorsFromThrown(inviteResult.reason));
    } else if (inviteResult.value) {
      // Already-waiting recipients are an honest DONE-state line, never failures: the
      // person accepted this subject's share earlier and sits in the admin queue —
      // nothing new was sent to them.
      waiting = inviteResult.value.alreadyWaiting ?? [];
      if (!inviteResult.value.success && inviteResult.value.errors.length === 0) {
        emails.forEach(c => failedKeys.add(c.key));
        failures.push({ code: 'INTERNAL_ERROR', message: this.translate.instant('common.errorGeneric') });
      }
      // Per-ADDRESS rows (a mid-batch rate-limit trip, an already-member address): only the
      // named address fails — calling delivered people failed would break the partial
      // state's honest-copy contract. A row without an address is gesture-level and fails
      // the lane. A row refused for a SEAT (the plan's member limit; its own text is the
      // server's words) is kept apart: the one limit face speaks for it, and it is not retried.
      for (const error of inviteResult.value.errors) {
        const failedEmail = error.args?.['email'];
        if (error.code === 'QUOTA_EXCEEDED' && failedEmail) {
          seatRefused.add(failedEmail.toLowerCase());
          continue;
        }
        failures.push(error);
        if (failedEmail) failedKeys.add(failedEmail.toLowerCase());
        else emails.forEach(c => failedKeys.add(c.key));
      }
    }

    if (failedKeys.size === 0 && seatRefused.size === 0) {
      this.alreadyWaiting.set(waiting);
      // "Sent." must only headline a gesture that actually delivered something — member
      // shares, minted invitations, or refreshed re-sends all count; a gesture whose
      // every recipient was already waiting sent nothing.
      this.anythingSent.set(recipients.length > waiting.length);
      this.phase.set('done');
      return;
    }

    // The waiting disclosure survives a partial/failed gesture too — those recipients
    // really were resolved (nothing new sent, they sit in the queue), and dropping the
    // line whenever ANY other recipient failed would hide a true outcome.
    this.alreadyWaiting.set(waiting);
    // Keep ONLY the failed people for the retry, and mint a FRESH key — the completed
    // gesture's key would replay-hit and silently skip everyone on a re-Send. The people
    // the brand had no room for are listed apart and never retried.
    this.recipients.set(recipients.filter(c => failedKeys.has(c.key)));
    this.seatRefusedChips.set(recipients.filter(c => seatRefused.has(c.key)));
    this.idempotencyKey = crypto.randomUUID();
    // The other refusals' own sentences; the seat refusals speak through the face the non-empty list mounts.
    const sentences = failures.map(e => resolveErrorMessage(e, (k, p) => this.translate.instant(k, p)));
    this.failureMessages.set([...new Set(sentences)]);
    // 'partial' means someone NEW received something: the people already waiting were resolved before this gesture, and
    // the refused and the seat-refused got nothing. An address can sit in two of those sets, so the union is read, never a sum.
    const stopped = new Set<string>([...failedKeys, ...seatRefused, ...waiting.map(email => email.toLowerCase())]);
    this.phase.set(recipients.some(c => !stopped.has(c.key)) ? 'partial' : 'failed');
  }

  private errorsFromThrown(reason: unknown): UserError[] {
    if (reason instanceof MutationError) return reason.userErrors;
    return [{ code: 'INTERNAL_ERROR', message: this.translate.instant('common.errorGeneric') }];
  }

  protected retry(): void {
    this.phase.set('configure'); // failed chips were retained; the next Send is a new gesture
  }

  /**
   * Ctrl/⌘+Enter anywhere in the dialog sends — the chord every composer in the app answers to.
   * Bound on the dialog element itself: content and footer both descend from it (the app leaves
   * PrimeNG's overlayAppendTo at its default, so the container renders inside the host element).
   */
  protected onDialogKeydown(event: KeyboardEvent): void {
    // A press the people field already consumed (a keyboard-born pick) is left alone —
    // its second press sends.
    if (event.isComposing || event.defaultPrevented || !isSubmitChord(event)) return;
    if (this.phase() !== 'configure' || !this.canSend()) return;
    event.preventDefault();
    void this.send();
  }

  /**
   * Would closing now lose typed work? Only the configure phase holds any: recipients, a message,
   * or text typed into the people field and not yet committed to a chip. An in-flight send is
   * excluded — the prompt's "won't be sent" would be false while the server is sending it.
   */
  protected isDirty(): boolean {
    return this.phase() === 'configure' && !this.sending()
      && (this.recipients().length > 0 || this.message().trim() !== '' || (this.chipInput()?.hasPendingText() ?? false));
  }

  /** Every DISMISS gesture (the X, Cancel/Close/Done, Escape) lands here. */
  guardedClose(): void {
    if (!this.isDirty()) { this.dialog.close(); return; }
    confirmDiscard(this.confirmation, this.translate, DISCARD_SHARE_COPY, () => this.dialog.close());
  }

  /** The already-waiting done-state line: one sentence for one person (their email — the
   *  recipients are outsiders, no display name resolves), the plural family above a
   *  per-recipient list (the failure-list shape). */
  protected alreadyWaitingSummary(): string {
    const waiting = this.alreadyWaiting();
    if (waiting.length === 1) {
      return this.translate.instant('share.alreadyWaitingSummary.one', { name: waiting[0] });
    }
    return this.translate.instant(
      pluralKey('share.alreadyWaitingSummary', waiting.length, this.locale.activeUILanguage()),
      { count: waiting.length });
  }

  /** Done-phase link: the sent shares live on /shares (member shares are instant and row-less). */
  protected viewSharesSent(): void {
    this.dialog.close();
    void this.router.navigate(['/shares']);
  }
}
